"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.openChartScreenshotPanel = openChartScreenshotPanel;
const chart_screenshot_1 = require("./chart-screenshot");
// Injected on first open only, so the page's render-blocking CSS is unchanged.
const PANEL_CSS = `
.arz-chart-shot{width:min(560px,calc(100vw - 32px));max-height:calc(100dvh - 32px);margin:auto;padding:16px;border:1px solid var(--arz-color-border);border-radius:var(--arz-radius-lg,12px);background:var(--arz-color-surface-elevated);color:var(--arz-color-text);box-shadow:var(--arz-shadow-md);box-sizing:border-box;overflow:auto;overscroll-behavior:contain;font:inherit;direction:rtl}
.arz-chart-shot::backdrop{background:var(--arz-color-overlay,rgba(0,0,0,.45))}
.arz-chart-shot__head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
.arz-chart-shot__title{margin:0;font-size:15px;font-weight:700;line-height:1.6}
.arz-chart-shot__close{min-height:40px;padding:6px 12px;border:0;border-radius:var(--arz-radius-md,8px);background:transparent;color:var(--arz-color-text-muted);font:inherit;font-size:13px;cursor:pointer}
.arz-chart-shot__preview{position:relative;display:flex;align-items:center;justify-content:center;width:100%;aspect-ratio:16/9;margin-inline:auto;overflow:hidden;border:1px solid var(--arz-color-border);border-radius:var(--arz-radius-md,8px);background:var(--arz-color-surface-subtle)}
.arz-chart-shot__preview[data-ratio="9:16"]{width:auto;height:min(52dvh,440px);aspect-ratio:9/16}
.arz-chart-shot__preview img{display:block;width:100%;height:100%;object-fit:contain}
.arz-chart-shot__preview img[hidden]{display:none}
.arz-chart-shot__status{min-height:1.8em;margin:10px 0 12px;color:var(--arz-color-text-muted);font-size:13px;line-height:1.8}
.arz-chart-shot__status[data-tone="error"]{color:var(--arz-color-danger-content,var(--arz-color-danger))}
.arz-chart-shot__actions{display:flex;gap:8px}
.arz-chart-shot__actions button{flex:1;min-height:44px;padding:.5em 1em;border-radius:var(--arz-radius-md,8px);font:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:background-color var(--arz-duration-fast,140ms) var(--arz-ease-standard,ease-out),border-color var(--arz-duration-fast,140ms) var(--arz-ease-standard,ease-out)}
.arz-chart-shot__primary{border:1px solid var(--arz-color-primary);background:var(--arz-color-primary);color:var(--arz-color-on-primary)}
.arz-chart-shot__outline{border:1px solid var(--arz-color-border-strong);background:var(--arz-color-surface);color:var(--arz-color-text)}
.arz-chart-shot__actions button:disabled{cursor:not-allowed;opacity:.5}
.arz-chart-shot__actions [hidden]{display:none}
.arz-chart-shot :focus-visible{outline:2px solid var(--arz-color-primary);outline-offset:2px}
@media (hover:hover){
.arz-chart-shot__close:hover{background:var(--arz-color-interactive-hover);color:var(--arz-color-text)}
.arz-chart-shot__primary:not(:disabled):hover{background:var(--arz-color-primary-hover);border-color:var(--arz-color-primary-hover)}
.arz-chart-shot__outline:not(:disabled):hover{background:var(--arz-color-interactive-hover)}
}
/* Skeleton: the image-sized frame shimmers under the shared brand loader (logo-loader.css). */
.arz-chart-shot__loader{--arz-logo-loader-size:44px;position:absolute;inset:0;z-index:1;margin:auto}
.arz-chart-shot__preview:not([aria-busy="true"]) .arz-chart-shot__loader{display:none}
.arz-chart-shot__preview[aria-busy="true"]::after{content:"";position:absolute;inset:0;background:linear-gradient(100deg,transparent 20%,color-mix(in srgb,var(--arz-color-surface) 72%,transparent) 48%,transparent 76%);transform:translateX(-110%);animation:arz-chart-shot-skeleton 1.45s ease-in-out infinite}
@keyframes arz-chart-shot-skeleton{to{transform:translateX(110%)}}
@media (prefers-reduced-motion:reduce){.arz-chart-shot__preview[aria-busy="true"]::after{animation:none;transform:none;background:none}}
@media (max-width:600px){
.arz-chart-shot{width:100%;max-width:100%;margin:auto 0 0;border-width:1px 0 0;border-radius:var(--arz-radius-lg,12px) var(--arz-radius-lg,12px) 0 0;padding-bottom:calc(16px + env(safe-area-inset-bottom))}
}`;
let panel = null;
let current = null;
let session = 0;
let file = null;
let objectUrl = '';
const setStatus = (text, tone = 'info') => {
    if (!panel)
        return;
    panel.status.textContent = text;
    panel.status.dataset.tone = tone;
};
const save = () => {
    if (!file)
        return;
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = file.name;
    link.hidden = true;
    panel.dialog.appendChild(link);
    link.click();
    link.remove();
    setStatus(chart_screenshot_1.SCREENSHOT_TEXT.saved);
};
const share = () => {
    if (!file || !panel)
        return;
    const shared = file;
    navigator.share({ files: [shared], title: current === null || current === void 0 ? void 0 : current.title }).then(() => { if (file === shared)
        panel === null || panel === void 0 ? void 0 : panel.dialog.close(); }, 
    // AbortError: the visitor closed the share sheet; anything else falls back to saving.
    (error) => { if ((error === null || error === void 0 ? void 0 : error.name) !== 'AbortError' && file === shared)
        save(); });
};
function ensurePanel() {
    if (panel)
        return panel;
    const style = document.createElement('style');
    style.textContent = PANEL_CSS;
    document.head.appendChild(style);
    const dialog = document.createElement('dialog');
    dialog.className = 'arz-chart-shot';
    dialog.dir = 'rtl';
    dialog.setAttribute('aria-labelledby', 'arz-chart-shot-title');
    dialog.innerHTML = `
    <div class="arz-chart-shot__head">
      <h2 class="arz-chart-shot__title" id="arz-chart-shot-title">${chart_screenshot_1.SCREENSHOT_TEXT.title}</h2>
      <button type="button" class="arz-chart-shot__close">بستن</button>
    </div>
    <div class="arz-chart-shot__preview"><span class="arz-logo-loader arz-chart-shot__loader" aria-hidden="true"></span><img alt="پیش نمایش تصویر نمودار" hidden></div>
    <p class="arz-chart-shot__status" role="status"></p>
    <div class="arz-chart-shot__actions">
      <button type="button" class="arz-chart-shot__primary" data-action="share" hidden>اشتراک گذاری</button>
      <button type="button" class="arz-chart-shot__outline" data-action="save">ذخیره تصویر</button>
      <button type="button" class="arz-chart-shot__primary" data-action="retry" hidden>تلاش دوباره</button>
    </div>`;
    panel = {
        dialog,
        preview: dialog.querySelector('.arz-chart-shot__preview'),
        image: dialog.querySelector('.arz-chart-shot__preview img'),
        status: dialog.querySelector('.arz-chart-shot__status'),
        share: dialog.querySelector('[data-action="share"]'),
        save: dialog.querySelector('[data-action="save"]'),
        retry: dialog.querySelector('[data-action="retry"]'),
    };
    dialog.querySelector('.arz-chart-shot__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog)
        dialog.close(); });
    panel.share.addEventListener('click', share);
    panel.save.addEventListener('click', save);
    panel.retry.addEventListener('click', () => {
        const retry = current === null || current === void 0 ? void 0 : current.retry;
        dialog.close();
        retry === null || retry === void 0 ? void 0 : retry();
    });
    // `close` fires asynchronously: skip it when the panel was already reopened (retry).
    dialog.addEventListener('close', () => {
        if (dialog.open)
            return;
        // Ignore a render that finishes after the panel closed.
        session += 1;
        releaseImage();
        current === null || current === void 0 ? void 0 : current.returnFocus.focus({ preventScroll: true });
    });
    return panel;
}
function releaseImage() {
    file = null;
    if (objectUrl)
        URL.revokeObjectURL(objectUrl);
    objectUrl = '';
    panel === null || panel === void 0 ? void 0 : panel.image.removeAttribute('src');
}
function openChartScreenshotPanel(request) {
    const view = ensurePanel();
    if (view.dialog.open)
        view.dialog.close();
    const id = ++session;
    current = request;
    releaseImage();
    view.preview.dataset.ratio = request.aspectRatio;
    view.preview.setAttribute('aria-busy', 'true');
    view.image.hidden = true;
    view.share.hidden = true;
    view.save.hidden = false;
    view.save.disabled = true;
    view.retry.hidden = true;
    setStatus(chart_screenshot_1.SCREENSHOT_TEXT.loading);
    // Inside fullscreen (itself a modal dialog) the panel nests in it to stay on top.
    (request.returnFocus.closest('dialog[open]') || document.body).appendChild(view.dialog);
    window.arzDialogOverlay.open(view.dialog);
    request.blob.then((blob) => {
        if (id !== session)
            return;
        file = new File([blob], request.fileName, { type: blob.type || 'image/png' });
        objectUrl = URL.createObjectURL(blob);
        view.image.src = objectUrl;
        view.image.hidden = false;
        view.preview.removeAttribute('aria-busy');
        view.save.disabled = false;
        view.share.hidden = !(0, chart_screenshot_1.canShareImageFile)(navigator, file);
        (view.share.hidden ? view.save : view.share).focus({ preventScroll: true });
        setStatus(chart_screenshot_1.SCREENSHOT_TEXT.ready);
        request.copied.then((copied) => { if (copied && id === session)
            setStatus(chart_screenshot_1.SCREENSHOT_TEXT.copied); });
    }, () => {
        if (id !== session)
            return;
        view.preview.removeAttribute('aria-busy');
        view.save.hidden = true;
        view.retry.hidden = false;
        view.retry.focus({ preventScroll: true });
        setStatus(chart_screenshot_1.SCREENSHOT_TEXT.error, 'error');
    });
}
